import PropTypes from "prop-types";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useShopFilters } from "../hooks/index.js";
import { partitionIntoSegments } from "../utils.js";
import CharacterGroup from "./CharacterGroup.jsx";
import cg from "./CharacterGroup.module.css";
import cs from "./CompactSection.module.css";
import FeaturedSection from "./FeaturedSection.jsx";
import { NoResultsIcon } from "./Icons.jsx";
import ProductCard from "./ProductCard.jsx";
import pc from "./ProductCard.module.css";
import s from "./ProductSection.module.css";
import { Button, Skeleton } from "../../../themeV2";

const BREAKPOINT_MD = 768;
const BREAKPOINT_LG = 1200;
const GRID_LIMIT_SM = 4; // 2 cols × 2 rows
const GRID_LIMIT_MD = 6; // 3 cols × 2 rows
const GRID_LIMIT_LG = 8; // 4 cols × 2 rows

function getGridLimit() {
  if (typeof window === "undefined") return GRID_LIMIT_LG;
  if (window.innerWidth <= BREAKPOINT_MD) return GRID_LIMIT_SM;
  if (window.innerWidth <= BREAKPOINT_LG) return GRID_LIMIT_MD;
  return GRID_LIMIT_LG;
}

function useGridLimit() {
  const [limit, setLimit] = useState(getGridLimit);
  useEffect(() => {
    const handler = () => setLimit(getGridLimit());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return limit;
}

function SkeletonCard() {
  return (
    <div className={pc.card} style={{ pointerEvents: "none" }}>
      <div className={pc.cardImg}>
        <Skeleton
          variant="rect"
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "var(--shape-md) var(--shape-md) 0 0",
          }}
        />
      </div>
      <div className={pc.body}>
        <Skeleton variant="text" style={{ width: "85%", marginBottom: 8 }} />
        <Skeleton variant="text" style={{ width: "60%", marginBottom: 4 }} />
        <Skeleton variant="text" style={{ width: "45%" }} />
        <div className={pc.footer}>
          <Skeleton variant="text" size="lg" style={{ width: 48 }} />
          <Skeleton
            variant="rect"
            style={{ width: 72, height: 36, borderRadius: "var(--shape-xl)" }}
          />
        </div>
      </div>
    </div>
  );
}

function SkeletonGroup() {
  return (
    <div>
      <div className={cg.header}>
        <Skeleton variant="text" size="lg" style={{ width: 140 }} />
        <Skeleton variant="text" style={{ width: 100 }} />
      </div>
      <div className={cg.grid}>
        {Array.from({ length: 4 }, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  );
}

function CompactSection({ groups, gridColumns, onProductClick }) {
  return (
    <div className={cs.zone} role="group" aria-label="Additional characters">
      {groups.map((group) => {
        const isGeneral = group.character === "General";
        const colSpan = Math.min(group.products.length, gridColumns);

        return (
          <div
            key={group.character}
            className={cs.subGroup}
            style={{ gridColumn: `span ${colSpan}` }}
          >
            <div className={cs.subHeader}>
              {group.franchise && (
                <span className={cs.subFranchise}>{group.franchise}</span>
              )}
              <span className={cs.subName}>{group.character}</span>
              {!isGeneral && group.vas.length > 0 && (
                <span className={cs.subVa}>
                  {group.vas.map((va, i) => (
                    <Fragment key={va.name}>
                      {i > 0 && ", "}
                      <Link to={`/${va.route}`} className={cs.subVaLink}>
                        {va.name}
                      </Link>
                    </Fragment>
                  ))}
                </span>
              )}
            </div>
            <div className={cs.subProducts}>
              {group.products.map((product) => (
                <ProductCard
                  key={product.product_id}
                  product={product}
                  onClick={onProductClick}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

CompactSection.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      character: PropTypes.string.isRequired,
      franchise: PropTypes.string,
      vas: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          route: PropTypes.string,
        }),
      ).isRequired,
      products: PropTypes.array.isRequired,
    }),
  ).isRequired,
  gridColumns: PropTypes.number.isRequired,
  onProductClick: PropTypes.func.isRequired,
};

export default function ProductSection({ isLoading, onProductClick }) {
  const gridLimit = useGridLimit();
  const gridColumns = gridLimit / 2;
  const {
    filteredGroups,
    featuredGroup,
    groupsKey,
    totalVisible,
    filterLabels,
    hasActiveFilters,
    handleClearAll,
  } = useShopFilters();

  if (isLoading) {
    return (
      <>
        <div className={s.sectionBar}>
          <div className={s.sectionMeta}>
            <Skeleton
              variant="text"
              as="span"
              style={{ width: 120, display: "inline-block" }}
            />
          </div>
        </div>
        <div className={cg.groups}>
          <SkeletonGroup />
          <SkeletonGroup />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={s.sectionBar}>
        <div className={s.sectionMeta} aria-live="polite">
          <span>Showing {totalVisible} products</span>
          {filterLabels.length > 0 && (
            <span className={s.activeFilters}> {filterLabels.join(" · ")}</span>
          )}
        </div>
      </div>

      <div key={groupsKey} className={s.section}>
        {filteredGroups.length === 0 && !featuredGroup ? (
          <div className={cg.groups}>
            <div className={s.emptyState}>
              <NoResultsIcon className={s.emptyIcon} />
              <p className={s.emptyHeading}>No products found</p>
              <p className={s.emptySub}>
                Try adjusting your filters or search to find what you&apos;re
                looking for.
              </p>
              {hasActiveFilters && (
                <Button
                  size="sm"
                  className={s.emptyCta}
                  onClick={handleClearAll}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {featuredGroup && (
              <FeaturedSection
                group={featuredGroup}
                onProductClick={onProductClick}
              />
            )}
            <div className={cg.groups}>
              {partitionIntoSegments(filteredGroups).map((segment, i) =>
                segment.type === "full" ? (
                  <CharacterGroup
                    key={segment.group.character}
                    group={segment.group}
                    onProductClick={onProductClick}
                    defaultExpanded={hasActiveFilters}
                    gridLimit={gridLimit}
                  />
                ) : (
                  <CompactSection
                    key={`compact-${i}`}
                    groups={segment.groups}
                    gridColumns={gridColumns}
                    onProductClick={onProductClick}
                  />
                ),
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

ProductSection.propTypes = {
  isLoading: PropTypes.bool,
  onProductClick: PropTypes.func.isRequired,
};
